import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

const SectionScrollIndicator = () => {
  const [activeSection, setActiveSection] = useState(0);
  const [scrollProgress, setScrollProgress] = useState(0);
  const location = useLocation();
  const navigate = useNavigate();

  const sections = [
    { 
      path: '/', 
      label: 'Home',
      color: 'bg-primary'
    },
    { 
      path: '/about', 
      label: 'About',
      color: 'bg-secondary'
    },
    { 
      path: '/skills', 
      label: 'Skills',
      color: 'bg-accent'
    },
    { 
      path: '/projects', 
      label: 'Projects',
      color: 'bg-success'
    },
    { 
      path: '/experience', 
      label: 'Experience',
      color: 'bg-warning'
    },
    { 
      path: '/blog', 
      label: 'Blog',
      color: 'bg-primary'
    },
    { 
      path: '/contact', 
      label: 'Contact',
      color: 'bg-primary'
    }
  ];

  useEffect(() => {
    const currentSectionIndex = sections.findIndex(section => section.path === location.pathname);
    if (currentSectionIndex !== -1) {
      setActiveSection(currentSectionIndex);
    }
  }, [location.pathname]);

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.pageYOffset;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const scrollPercent = (scrollTop / docHeight) * 100;
      setScrollProgress(Math.min(scrollPercent, 100));
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const progressPercentage = ((activeSection + 1) / sections.length) * 100;

  return (
    <>
      {/* Desktop Progress Bar */}
      <div className="hidden lg:block fixed top-16 left-0 right-0 z-dropdown">
        <div className="h-1 bg-muted">
          <div 
            className="h-full bg-primary transition-all duration-500 ease-out"
            style={{ width: `${progressPercentage}%` }}
          />
        </div>
      </div>

      {/* Mobile/Tablet Dot Navigation */}
      <div className="lg:hidden fixed right-1 sm:right-3 top-1/2 transform -translate-y-1/2 z-dropdown pointer-events-none">
        <div className="flex flex-col space-y-2 sm:space-y-3">
          {sections.map((section, index) => (
            <div
              key={section.path}
              className={`section-indicator ${index === activeSection ? 'active' : ''} pointer-events-auto`}
              title={section.label}
              onClick={() => navigate(section.path)}
              style={{ cursor: 'pointer' }}
            />
          ))}
        </div>
      </div>

      {/* Desktop Side Navigation */}
      <div className="hidden xl:block fixed left-6 top-1/2 transform -translate-y-1/2 z-dropdown">
        <div className="flex flex-col space-y-4">
          {sections.map((section, index) => (
            <div
              key={section.path}
              className="flex items-center group cursor-pointer"
              title={section.label}
              onClick={() => navigate(section.path)}
              style={{ cursor: 'pointer' }}
            >
              <div className={`section-indicator ${index === activeSection ? 'active' : ''} mr-3`} />
              <span className={`text-sm font-medium transition-all duration-200 opacity-0 group-hover:opacity-100 transform translate-x-2 group-hover:translate-x-0 ${
                index === activeSection ? 'text-primary opacity-100 translate-x-0' : 'text-text-secondary'
              }`}>
                {section.label}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Scroll Progress Circle (Mobile) */}
      <div className="lg:hidden fixed bottom-6 right-4 sm:right-6 z-dropdown">
        <div className="relative w-10 h-10 sm:w-12 sm:h-12">
          <svg className="w-10 h-10 sm:w-12 sm:h-12 transform -rotate-90" viewBox="0 0 36 36">
            <path
              className="text-muted"
              stroke="currentColor"
              strokeWidth="2"
              fill="none"
              d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
            />
            <path 
              className="text-primary"
              stroke="currentColor"
              strokeWidth="2"
              strokeDasharray={`${scrollProgress}, 100`}
              strokeLinecap="round"
              fill="none"
              d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
            />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="text-xs font-medium text-text-primary">
              {Math.round(scrollProgress)}%
            </span>
          </div>
        </div>
      </div>
    </>
  );
};

export default SectionScrollIndicator;